import { computed, ref } from 'vue';
import type { Customer, CustomerProfile } from '@kcalc/lib/browser';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/composables/firebase/auth';
import { userId$ } from '@/lib/firebase/auth';

const { user } = useAuth();

const demoAccount = ref<undefined | null | Customer>(undefined);
const hasCheckedForDemoAccount = computed(() => demoAccount.value !== undefined);
const hasDemoAccount = computed(() => !!demoAccount.value);
const emailBlocked = ref(false);

const canCreateDemoAccount = computed(() => {
  if (emailBlocked.value) {
    return false;
  }

  if (hasCheckedForDemoAccount.value && !hasDemoAccount.value) {
    return true;
  }

  return false;
});

async function fetchDemoAccountInfo() {
  if (user.value) {
    apiClient.consultant.customer.demo.get().then(({ data }) => {
      if (user.value && data) {
        if ('status' in data && data.status === 'error') {
          emailBlocked.value = true;
        } else {
          demoAccount.value = {
            id: user.value.authUser.uid,
            profile: data as CustomerProfile,
          };
        }
      } else {
        demoAccount.value = null;
      }
    });
  }
}

// reset if the authenticated user changes
userId$.subscribe((value) => {
  if (!value && demoAccount.value !== undefined) {
    demoAccount.value = undefined;
    emailBlocked.value = false;
  } else if (value && demoAccount.value === undefined) {
    fetchDemoAccountInfo();
  }
});

async function createDemoAccount() {
  if (user.value) {
    const { data, error } = await apiClient.consultant.customer.demo.put();

    if (error) {
      throw new Error();
    }

    if (data) {
      demoAccount.value = {
        id: user.value.authUser.uid,
        profile: data,
      };
    }

    return demoAccount;
  }
}

export function useDemoAccount() {
  return {
    demoAccount,
    hasDemoAccount,
    hasCheckedForDemoAccount,
    createDemoAccount,
    canCreateDemoAccount,
  };
}
